import { Button, styled, Typography } from '@mui/material'
import { useCallback } from 'react'
import { useFormContext } from 'react-hook-form'
import { theme } from '../../../../../../../modules/App/Providers/Theme/theme'
import { Check as CheckIcon } from '@mui/icons-material'
import { addMinutes, differenceInMinutes, isEqual, setHours, setMinutes, startOfDay } from 'date-fns'
import { useFormat, useLocale } from '../../../../../../../hooks'
import { ResourceAvailability } from '../../../../../../../types/mergedAvailabilities'
import { AppointmentFormData } from '../../../../../../../types'

type Props = {
  slot: ResourceAvailability
}

const standardDateFormatOptions: Intl.DateTimeFormatOptions = { hour: '2-digit', hourCycle: 'h12', minute: '2-digit' }

const StyledButton = styled(Button)`
  &[data-selected='true'] {
    background-color: ${({ theme }) => theme.palette.primary.main};
    color: ${({ theme }) => theme.palette.common.white};

    & * [x-duration] {
      color: ${theme.palette.grey[200]};
    }
  }
`

const Check = styled(CheckIcon)`
  justify-self: start;
  margin-right: 0.5rem;
  left: 0rem;
  opacity: 0;
  transform: scale(0%);
  transition: 200ms transform ease-in;

  &[data-show='true'] {
    left: 0.25rem;
    opacity: 1;
    transform: scale(100%);
  }
`

const Container = styled('div')`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  grid-auto-flow: column;
  width: 100%;
`

const Item = ({ slot }: Props) => {
  const { hourCycle } = useLocale()
  const { date_end, date_start, resource_ids } = slot
  const blockDurationInMinutes = differenceInMinutes(date_end, date_start)

  const { setValue, watch } = useFormContext<AppointmentFormData>()
  const [timeZone, durationInMinutes, clientStartDate, clientStartTime] = watch([
    'customer_timezone',
    'duration_in_minutes',
    'clientStartDate',
    'clientStartTime',
  ]) as [string, number, Date, Date]

  const { formatDate: formatCustomerDate } = useFormat({
    dateOptions: { ...standardDateFormatOptions, hourCycle, timeZone },
  })

  const customerDisplayDate = formatCustomerDate(date_start)
  const isSelected = isEqual(clientStartTime, clientStartDate) && isEqual(date_end, addMinutes(clientStartDate, durationInMinutes))
  const onClick = useCallback(() => {
    if (isSelected) {
      setValue('clientStartTime', undefined)
      return
    }

    const startTimeWithHourMinutes = setMinutes(setHours(startOfDay(date_start), date_start.getHours()), date_start.getMinutes())

    setValue('clientStartDate', startTimeWithHourMinutes)
    setValue('clientStartTime', startTimeWithHourMinutes)
    setValue('duration_in_minutes', blockDurationInMinutes)
    setValue('duration_in_minutes_temp', blockDurationInMinutes)
    setValue('resource_ids', resource_ids)
    setValue('start_date', date_start)
  }, [date_start, blockDurationInMinutes, isSelected, setValue])

  return (
    <li>
      <StyledButton data-date={date_start.toISOString()} data-selected={isSelected} onClick={onClick} fullWidth variant="outlined">
        <Container>
          {isSelected ? <Check data-show /> : <span />}
          <Typography>{customerDisplayDate}</Typography>
        </Container>
      </StyledButton>
    </li>
  )
}

export { Item }
